import { AnimationCallbackEvent, OnDestroy } from '@angular/core';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { ZalamoTooltipService } from '../../../zalamo-tooltip/zalamo-tooltip.service';
import { VisualGuidesInlineListComponent } from 'ZalamoLibrary/src/lib/ui/visual-guide/visual-guides-inline-list/visual-guides-inline-list.component';
import { takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'lib-support-icon',
    templateUrl: './support-icon.component.html',
    styleUrls: ['./support-icon.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SupportIconComponent implements OnInit, OnDestroy {
  @Output() showSupport = new EventEmitter<string | boolean>();
  @Input() helpOpen = false;
  shouldShowIconChat$ = new BehaviorSubject(false);
  shouldShowIconFAQ$ = new BehaviorSubject(false);
  shouldShowVisualGuidesList$ = new BehaviorSubject(false);
  @Input() visualGuidesCount = 0;
  mouseOverMe$ = new BehaviorSubject(false);
  @Input() chatBadge = new ReplaySubject(1);
  private _animState = false;
  private bounceAnimation?: Animation;
  private bounceTimer?: ReturnType<typeof setTimeout>;
  @ViewChild('chatBadgeIcon') private chatBadgeIcon?: ElementRef<HTMLElement>;
  get animState(): boolean { return this._animState; }
  set animState(value: boolean) {
    if (this._animState === value) return;
    this._animState = value;
    const element = this.chatBadgeIcon?.nativeElement;
    if (!element || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.bounceAnimation?.cancel();
    this.bounceAnimation = element.animate([
      { transform: 'translateY(0)', offset: 0 }, { transform: 'translateY(0)', offset: .2 },
      { transform: 'translateY(-30px)', offset: .4 }, { transform: 'translateY(-30px)', offset: .43 },
      { transform: 'translateY(0)', offset: .53 }, { transform: 'translateY(-15px)', offset: .7 },
      { transform: 'translateY(0)', offset: .8 }, { transform: 'translateY(-4px)', offset: .9 },
      { transform: 'translateY(0)', offset: 1 }
    ], { duration: 1000, easing: 'cubic-bezier(.215,.61,.355,1)' });
    this.bounceAnimation.finished.then(() => this.animDone(), () => {});
  }
  showChatIcon(event: AnimationCallbackEvent): void {
    const duration = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 200;
    const animation = event.target.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(-50px)' }], { duration, easing: 'linear' });
    animation.finished.catch(() => {}).finally(() => { this.chatIconVisible(); event.animationComplete(); });
  }
  ngOnDestroy(): void { clearTimeout(this.bounceTimer); this.bounceAnimation?.cancel(); }

  @Input() isChatContainerOpen = false;
  @ViewChild('visualGuidesIcon', { static: false })
  visualGuidesIcon: ElementRef<HTMLElement>;
  visualGuidesListOpen = false;

  _chatOpen = false;

  get chatOpen() {
    return this._chatOpen;
  }

  @Input() set chatOpen(open) {
    this._chatOpen = open;
    this.shouldShowIconChat$.next(this.mouseOverMe$.value || open);
  }

  _unreadMessagesCount = 0;

  @Input() set unreadMessagesCount(count: number) {
    this._unreadMessagesCount = count;
    this.chatBadge.next(count);
  }

  private _visualGuideOpen = false;

  get visualGuideOpen(): boolean {
    return this._visualGuideOpen;
  }

  @Input() set visualGuideOpen(value: boolean) {
    this._visualGuideOpen = value;
    this.shouldShowVisualGuidesList$.next(value);
  }

  _faqOpen = false;

  get faqOpen() {
    return this._faqOpen;
  }

  @Input() set faqOpen(open) {
    this._faqOpen = open;
    this.shouldShowIconFAQ$.next(open);
  }

  get faqState() {
    if (!this.shouldShowIconFAQ$.value) {
      return 'closed';
    }
    if (this.shouldShowIconFAQ$.value && this.shouldShowIconChat$.value) {
      return 'open';
    }
    return 'half-open';
  }

  get visualGuidesState() {
    if (!this.shouldShowVisualGuidesList$.value) {
      return 'closed';
    }
    if (
      this.shouldShowVisualGuidesList$.value &&
      this.shouldShowIconChat$.value
    ) {
      return 'open';
    }
    return 'half-open';
  }

  get chatState() {
    if (!this.shouldShowIconChat$.value) {
      return 'closed';
    }
    if (this.shouldShowIconFAQ$.value && this.shouldShowIconChat$.value) {
      return 'open';
    }
    return 'half-open';
  }

  constructor(private zalamoTooltipService: ZalamoTooltipService) {}

  animDone() {
    this.bounceTimer = setTimeout(() => {
      if (!this.isChatContainerOpen && this._unreadMessagesCount > 0) {
        this.animState = !this.animState;
      }
    }, 5000);
  }

  @HostListener(':pointerenter') pointerEnter() {
    console.log('pointer enter');
    this.mouseOverMe$.next(true);
    this.shouldShowIconChat$.next(true);
    this.shouldShowIconFAQ$.next(true);
    this.shouldShowVisualGuidesList$.next(true);
  }

  chatIconVisible() {
    this.animState = this._unreadMessagesCount > 0;
  }

  @HostListener(':pointerleave') pointerLeave() {
    if (this.visualGuidesListOpen) {
      return;
    }
    this.mouseOverMe$.next(false);
    this.shouldShowIconChat$.next(this.chatOpen);
    this.shouldShowIconFAQ$.next(this.faqOpen);
    this.shouldShowVisualGuidesList$.next(this._visualGuideOpen);
  }

  showGuides() {
    this.visualGuidesListOpen = true;
    const ref = this.zalamoTooltipService.open(
      VisualGuidesInlineListComponent,
      'above',
      this.visualGuidesIcon.nativeElement
    );
    ref.componentInstance.picked
      .pipe(
        tap(() => ref.close()),
        takeUntil(ref.afterClosed())
      )
      .subscribe();
    ref
      .beforeClosed()
      .pipe(
        tap(() => {
          this.visualGuidesListOpen = false;
          this.mouseOverMe$.next(false);
          this.shouldShowIconChat$.next(this.chatOpen);
          this.shouldShowIconFAQ$.next(this.faqOpen);
          this.shouldShowVisualGuidesList$.next(this._visualGuideOpen);
        }),
        takeUntil(ref.afterClosed())
      )
      .subscribe(() => {});
  }

  ngOnInit(): void {}
}
